import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logga from '../assets/images/logga.png';
import ContactModal from './ContactModal';
import useAnalytics from '../hooks/useAnalytics';
import config from '../config/config';

interface CmsPage {
  id: number;
  title: string;
  slug: string;
  show_in_navbar: boolean;
  order_index: number;
}

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { trackInteraction } = useAnalytics();
  const [cmsPages, setCmsPages] = useState<CmsPage[]>([]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    setIsAdmin(!!token);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrolled = window.scrollY > 50;
      setIsScrolled(currentScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await fetch(`${config.api.baseUrl}/cms/pages`);
        if (!response.ok) throw new Error('Kunde inte hämta sidorna');
        const data = await response.json();
        setCmsPages(data.filter((page: CmsPage) => page.show_in_navbar));
      } catch (error) {
        console.error('Error fetching CMS pages:', error);
      }
    };

    fetchPages();
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navigationItems = [
    { title: 'Hem', path: '/' },
    { title: 'Tjänster', path: '/tjanster' },
    { title: 'Om Oss', path: '/om-oss' },
    ...(isAdmin ? [{ title: 'Admin', path: '/admin/dashboard' }] : [])
  ];

  const handleMenuItemClick = (path: string) => {
    setMobileMenuOpen(false);
    trackInteraction('navigation_click', { destination: path });
  };

  const handleContactClick = () => {
    setIsModalOpen(true);
    setMobileMenuOpen(false);
    trackInteraction('contact_button_click');
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('adminToken');
    setIsAdmin(false);
    if (location.pathname.startsWith('/admin')) {
      navigate('/admin/login');
    }
  };

  const menuItems = [
    ...navigationItems,
    ...cmsPages.sort((a, b) => a.order_index - b.order_index)
      .map(page => ({ title: page.title, path: `/sidor/${page.slug}` }))
  ];

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '300px',
          backgroundColor: theme.palette.primary.main,
          color: 'white'
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          onClick={() => setMobileMenuOpen(false)}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ mt: 2 }}>
        {menuItems.map((item) => (
          <ListItem 
            key={item.path}
            component={RouterLink}
            to={item.path}
            onClick={() => handleMenuItemClick(item.path)}
            sx={{
              borderLeft: isActive(item.path) ? `4px solid ${theme.palette.custom.orange}` : '4px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              },
              color: 'white',
              textDecoration: 'none'
            }}
          >
            <ListItemText 
              primary={item.title}
              primaryTypographyProps={{
                sx: {
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 600,
                  color: 'white'
                }
              }}
            />
          </ListItem>
        ))}
        <ListItem sx={{ mt: 2 }}>
          <Button
            onClick={handleContactClick}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: theme.palette.custom.orange,
              color: 'white',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.custom.orange,
                filter: 'brightness(0.9)'
              }
            }}
          >
            Kontakta Oss
          </Button>
        </ListItem>
        {isAuthenticated && (
          <ListItem sx={{ mt: 2 }}>
            {isAdmin && !location.pathname.includes('/admin/login') && (
              <Button
                onClick={handleLogout}
                variant="outlined"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: theme.palette.custom.orange,
                    color: theme.palette.custom.orange
                  }
                }}
              >
                Logga ut
              </Button>
            )}
          </ListItem>
        )}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          background: location.pathname === '/' 
            ? (isScrolled ? theme.palette.primary.main : 'transparent')
            : theme.palette.primary.main,
          boxShadow: isScrolled || location.pathname !== '/' ? 1 : 'none',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                minWidth: 'fit-content',
                mr: 6
              }}
            >
              <img 
                src={logga} 
                alt="DavidssonBVT Logo" 
                style={{ 
                  height: '40px', 
                  marginRight: '12px'
                }} 
              />
              <Typography
                className="logo-text"
                sx={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '2px',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Davidsson
              </Typography>
              <Typography
                className="bvt-text"
                sx={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
                  fontWeight: 900,
                  color: theme.palette.custom.orange,
                  letterSpacing: '2px',
                  ml: 1,
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                BVT
              </Typography>
            </Box>
            
            {!isMobile ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mr: 'auto' }}>
                  {menuItems.map((item) => (
                    <Button
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: 'white',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        position: 'relative',
                        padding: '6px 8px',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: isActive(item.path) ? '100%' : '0%',
                          height: '2px',
                          backgroundColor: theme.palette.custom.orange,
                          transition: 'width 0.3s ease-in-out'
                        },
                        '&:hover': {
                          backgroundColor: 'transparent',
                          '&::after': {
                            width: '100%'
                          }
                        }
                      }}
                    >
                      {item.title}
                    </Button>
                  ))}
                </Box>

                {location.pathname !== '/' && (
                  <>
                    <Button
                      onClick={handleContactClick}
                      variant="contained"
                      sx={{
                        backgroundColor: theme.palette.custom.orange,
                        color: 'white',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        padding: '8px 24px',
                        mr: isAdmin && isAuthenticated ? 2 : 0,
                        '&:hover': {
                          backgroundColor: theme.palette.custom.orange,
                          filter: 'brightness(0.9)'
                        }
                      }}
                    >
                      Kontakta Oss
                    </Button>
                    {isAdmin && isAuthenticated && !location.pathname.includes('/admin/login') && (
                      <Button
                        onClick={handleLogout}
                        variant="outlined"
                        sx={{
                          color: 'white',
                          borderColor: 'white',
                          '&:hover': {
                            borderColor: theme.palette.custom.orange,
                            color: theme.palette.custom.orange
                          }
                        }}
                      >
                        Logga ut
                      </Button>
                    )}
                  </>
                )}
              </>
            ) : (
              <Box sx={{ ml: 'auto' }}>
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{ color: 'white' }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {mobileMenu}
      <ContactModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Navbar; 